import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Detail from '../../components/Details/Detail';
import { Container, Box, Tabs, Tab, Button } from '@mui/material';
import { useAuthReady } from '../../hooks/useAuthReady';
import { ComprasAgilesService } from '../../services/compras-agiles.service';
import { type CompraAgilDetalleUi } from '../../schemas/compras-agiles/detalle';
import { useLoadingStore } from '../../store/loadingStore';
import BackButton from '../../components/Button/BackButton';
import { useAlertStore } from '../../store/alertStore';
import Archivos from '../../components/Details/Archivos';
import JsonMetadataTable from '../../components/Details/JsonMetadataTable';
import Notas from '../../components/Details/Notas';
import { useComprasAgilesStore } from '../../store/comprasAgilesStore';

interface Nota {
  id: string;
  fecha_creacion: string;
  mensaje: string;
  activo: boolean;
  usuario_autor: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ComprasAgilesDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [compraAgil, setCompraAgil] = useState<CompraAgilDetalleUi | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const isAuthReady = useAuthReady();
  const setLoading = useLoadingStore((state) => state.setLoading);
  const showAlert = useAlertStore((state) => state.showAlert);
  const fetchComprasAgiles = useComprasAgilesStore((state) => state.fetchComprasAgiles);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchDetail = useCallback(async () => {
    if (isAuthReady && id) {
      try {
        setLoading(true);
        const response = await ComprasAgilesService.fetchCompraAgilDetail(id);
        setCompraAgil(response);
      } catch (error) {
        console.error('Error fetching compra ágil detail:', error);
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        showAlert(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    }
  }, [id, isAuthReady, setLoading, showAlert]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleStatusChange = (newStatus: string) => {
    if (compraAgil) {
      setCompraAgil({
        ...compraAgil,
        estado_licitacion: newStatus
      });
    }
  };

  const handlePostulacionChange = async () => {
    if (!compraAgil) return;
    try {
      const response = await ComprasAgilesService.postular(compraAgil.codigo_externo);
      showAlert(response.message, 'success');
      await fetchDetail();
      await fetchComprasAgiles({ silent: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al postular';
      showAlert(errorMessage, 'error');
    }
  };

  const handleAdjudicacionChange = async () => {
    if (!compraAgil) return;
    try {
      const response = await ComprasAgilesService.adjudicar(compraAgil.codigo_externo);
      showAlert(response.message, 'success');
      await fetchDetail();
      await fetchComprasAgiles({ silent: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al adjudicar';
      showAlert(errorMessage, 'error');
    }
  };

  const handleFetchMensajes = async () => {
    if (!compraAgil) return [];
    try {
      return await ComprasAgilesService.listarMensajes(compraAgil.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar las notas';
      showAlert(errorMessage, 'error');
      return [];
    }
  };

  const handleCrearMensaje = async (mensaje: string): Promise<Nota[]> => {
    if (!compraAgil) return [];
    try {
      await ComprasAgilesService.crearMensaje(compraAgil.id, mensaje);
      showAlert('Nota creada correctamente', 'success');
      const nuevosMensajes = await ComprasAgilesService.listarMensajes(compraAgil.id);
      return nuevosMensajes;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear la nota';
      showAlert(errorMessage, 'error');
      throw error;
    }
  };

  const handleEliminarMensaje = async (mensajeId: string): Promise<Nota[]> => {
    if (!compraAgil) return [];
    try {
      await ComprasAgilesService.eliminarMensaje(mensajeId);
      showAlert('Nota eliminada correctamente', 'success');
      const nuevosMensajes = await ComprasAgilesService.listarMensajes(compraAgil.id);
      return nuevosMensajes;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al eliminar la nota';
      showAlert(errorMessage, 'error');
      throw error;
    }
  };

  if (!isAuthReady || !compraAgil) {
    return (
      <Container maxWidth={false}>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box sx={{ mb: 3, mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <BackButton to="/compras-agiles" label="Volver al listado" />
      </Box>
      
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        display: { xs: 'none', sm: 'block' }
      }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '48px',
              padding: '12px 16px'
            },
            '& .Mui-selected': {
              color: '#1976d2 !important',
            }
          }}
        >
          <Tab label="Resumen" />
          <Tab label="Archivos" />
          <Tab label="Datos Extra" />
          <Tab label="Notas" />
        </Tabs>
      </Box>

      <Box 
        sx={{ 
          display: { xs: 'flex', sm: 'none' },
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          '& .MuiButton-root': {
            flex: '1 1 auto',
            textTransform: 'none',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            padding: '6px 8px'
          }
        }}
      >
        <Button 
          variant={tabValue === 0 ? "contained" : "outlined"}
          onClick={() => setTabValue(0)}
          size="small"
        >
          Resumen
        </Button>
        <Button 
          variant={tabValue === 1 ? "contained" : "outlined"}
          onClick={() => setTabValue(1)}
          size="small"
        >
          Archivos
        </Button>
        <Button 
          variant={tabValue === 2 ? "contained" : "outlined"}
          onClick={() => setTabValue(2)}
          size="small"
        >
          Datos Extra
        </Button>
        <Button 
          variant={tabValue === 3 ? "contained" : "outlined"}
          onClick={() => setTabValue(3)}
          size="small"
        >
          Notas
        </Button>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Detail 
          nombre={compraAgil.nombre}
          resumen_llm={compraAgil.resumen_llm}
          resumen={compraAgil.resumen}
          resumen_general={compraAgil.resumen_general}
          fecha_creacion={compraAgil.fecha_creacion}
          estado_licitacion={compraAgil.estado_licitacion}
          codigo_externo={compraAgil.codigo_externo}
          onStatusChange={handleStatusChange}
          codigo_estado={compraAgil.codigo_estado}
          puntaje={compraAgil.puntaje}
          fecha_cierre={compraAgil.fecha_cierre}
          monto_estimado_string={compraAgil.monto_estimado}
          moneda={compraAgil.moneda}
          resumen_procesado_filtro_palabras={compraAgil.resumen_procesado_filtro_palabras}
          archivos_procesados={compraAgil.archivos_procesados}
          resumen_procesado_filtro_llm={compraAgil.resumen_procesado_filtro_llm}
          postulado={compraAgil.postulado}
          adjudicado={compraAgil.adjudicado}
          fecha_postulacion={compraAgil.fecha_postulacion}
          fecha_adjudicacion={compraAgil.fecha_adjudicacion}
          onPostulacionChange={handlePostulacionChange}
          onAdjudicacionChange={handleAdjudicacionChange}
          fetchDetail={fetchDetail}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Archivos 
          archivos={compraAgil.archivos} 
          entityType="compras-agiles" 
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <JsonMetadataTable 
          jsonMetadata={compraAgil.json_metadata}
          entityType="compras-agiles" 
          resumen={compraAgil.resumen ?? ""}
          resumen_llm={compraAgil.resumen_llm ?? ""}
          resumen_archivos_llm={compraAgil.resumen_archivos_llm ?? ""}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Notas 
          entityType="compras-agiles" 
          id={compraAgil.id}
          onFetchMensajes={handleFetchMensajes}
          onCrearMensaje={handleCrearMensaje}
          onEliminarMensaje={handleEliminarMensaje}
        />
      </TabPanel>
    </Container>
  );
};

export default ComprasAgilesDetail;
